import { useI18n } from '@ecom/i18n';
import {
  Badge,
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatCount, formatTnd } from '../format';
import { type DashboardAdmin } from '../types';

const TOP = 5;

/**
 * Every platform administrator with what it manages, what those shops sold and how many orders they made. The server ranks them by
 * shops managed; the screen shows the top five and offers the full list. A name opens that administrator's portfolio in the merchants
 * list (`?scope=<id>`). The server sends at most 100 rows: when more administrators exist the screen says so and points to the full list.
 */
export function AdminsTable({
  admins,
  total,
}: {
  readonly admins: readonly DashboardAdmin[];
  readonly total: number;
}): ReactElement {
  const { t, locale } = useI18n();
  const [showAll, setShowAll] = useState(false);

  if (admins.length === 0) return <EmptyState title={t('admin.dashboard.adm.none')} />;

  const shown = showAll ? admins : admins.slice(0, TOP);
  const title = showAll ? t('admin.dashboard.adm.allTitle') : t('admin.dashboard.adm.topTitle');

  return (
    <div className="flex flex-col gap-2" data-testid="dashboard-admins">
      <Table caption={title} captionVisible>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('admin.dashboard.adm.colAdmin')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.adm.colMerchants')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.adm.colActive')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.adm.colPending')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.adm.colSuspended')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.adm.colRevenue')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.adm.colOrders')}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((admin) => (
            <TableRow key={admin.userId}>
              <TableCell>
                <Link
                  to={`/merchants?scope=${admin.userId}`}
                  className="font-medium text-accent hover:underline"
                >
                  {admin.displayName ?? admin.email}
                </Link>
                {admin.displayName !== null ? (
                  <p className="text-xs text-inkFaint">{admin.email}</p>
                ) : null}
                {admin.status === 'active' ? null : (
                  <p className="mt-1">
                    <Badge tone="critical">{t('admin.dashboard.adm.disabledBadge')}</Badge>
                  </p>
                )}
              </TableCell>
              <TableCell align="end">{formatCount(admin.managed, locale)}</TableCell>
              <TableCell align="end">{formatCount(admin.active, locale)}</TableCell>
              <TableCell align="end">{formatCount(admin.pending, locale)}</TableCell>
              <TableCell align="end">{formatCount(admin.suspended, locale)}</TableCell>
              <TableCell align="end">{formatTnd(admin.revenue, locale)}</TableCell>
              <TableCell align="end">{formatCount(admin.orders, locale)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {admins.length > TOP ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowAll((value) => !value);
            }}
          >
            {showAll
              ? t('admin.dashboard.adm.showTop')
              : t('admin.dashboard.adm.showAll', { count: admins.length })}
          </Button>
        </div>
      ) : null}
      {total > admins.length ? (
        <p className="text-xs text-inkFaint" data-testid="dashboard-admins-capped">
          {t('admin.dashboard.adm.capped', { shown: admins.length, total })}{' '}
          <Link to="/administrators" className="text-accent hover:underline">
            {t('admin.dashboard.adm.cappedLink')}
          </Link>
        </p>
      ) : null}
    </div>
  );
}
